import type { GroceryItem, ListMetadata } from "../types/grocery";

const CACHE_KEY_PREFIX = "pantryverse.shared-list.session.v1.";
const CACHE_TTL_MS = 15 * 60 * 1000;

interface SerializedListMetadata {
  title?: string;
  created: string;
  expires?: string;
  accessConfig: ListMetadata["accessConfig"];
  isPermanent: boolean;
}

interface SerializedGroceryItem {
  id: string;
  name: string;
  category: string;
  categoryDisplayName?: string;
  isChecked: boolean;
  checkedAt?: string;
  createdAt: string;
  categorizationStatus?: GroceryItem["categorizationStatus"];
  updatedViaSharing?: boolean;
}

interface StoredSharedListSessionCache {
  cachedAt: string;
  metadata?: SerializedListMetadata;
  items?: SerializedGroceryItem[];
}

export interface SharedListSessionCache {
  metadata?: ListMetadata;
  items?: GroceryItem[];
}

const cacheKey = (listId: string): string => `${CACHE_KEY_PREFIX}${listId}`;

const storage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const parseDate = (value: unknown): Date | null => {
  if (typeof value !== "string") return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const deserializeMetadata = (value: unknown): ListMetadata | null => {
  if (!isRecord(value) || !isRecord(value.accessConfig)) return null;
  const created = parseDate(value.created);
  const expires =
    value.expires === undefined ? undefined : parseDate(value.expires);
  if (
    !created ||
    expires === null ||
    typeof value.accessConfig.allowEditing !== "boolean" ||
    typeof value.accessConfig.requireAuth !== "boolean" ||
    typeof value.isPermanent !== "boolean" ||
    (value.title !== undefined && typeof value.title !== "string")
  ) {
    return null;
  }
  return {
    ...(typeof value.title === "string" ? { title: value.title } : {}),
    created,
    ...(expires ? { expires } : {}),
    accessConfig: {
      allowEditing: value.accessConfig.allowEditing,
      requireAuth: value.accessConfig.requireAuth,
    },
    isPermanent: value.isPermanent,
  };
};

const deserializeItem = (value: unknown): GroceryItem | null => {
  if (!isRecord(value)) return null;
  const createdAt = parseDate(value.createdAt);
  const checkedAt =
    value.checkedAt === undefined ? undefined : parseDate(value.checkedAt);
  const categorizationStatus = value.categorizationStatus;
  if (
    !createdAt ||
    checkedAt === null ||
    typeof value.id !== "string" ||
    typeof value.name !== "string" ||
    typeof value.category !== "string" ||
    typeof value.isChecked !== "boolean" ||
    (value.categoryDisplayName !== undefined &&
      typeof value.categoryDisplayName !== "string") ||
    (value.updatedViaSharing !== undefined &&
      typeof value.updatedViaSharing !== "boolean") ||
    (categorizationStatus !== undefined &&
      categorizationStatus !== "pending" &&
      categorizationStatus !== "resolved" &&
      categorizationStatus !== "fallback")
  ) {
    return null;
  }
  return {
    id: value.id,
    name: value.name,
    category: value.category,
    ...(typeof value.categoryDisplayName === "string"
      ? { categoryDisplayName: value.categoryDisplayName }
      : {}),
    isChecked: value.isChecked,
    ...(checkedAt ? { checkedAt } : {}),
    createdAt,
    ...(categorizationStatus ? { categorizationStatus } : {}),
    ...(typeof value.updatedViaSharing === "boolean"
      ? { updatedViaSharing: value.updatedViaSharing }
      : {}),
  };
};

const readStoredCache = (
  listId: string,
): StoredSharedListSessionCache | null => {
  const sessionStorage = storage();
  if (!sessionStorage) return null;
  try {
    const raw = sessionStorage.getItem(cacheKey(listId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || typeof parsed.cachedAt !== "string") return null;
    return parsed as unknown as StoredSharedListSessionCache;
  } catch {
    return null;
  }
};

const writeStoredCache = (
  listId: string,
  value: Omit<StoredSharedListSessionCache, "cachedAt">,
): void => {
  const sessionStorage = storage();
  if (!sessionStorage) return;
  try {
    sessionStorage.setItem(
      cacheKey(listId),
      JSON.stringify({ ...value, cachedAt: new Date().toISOString() }),
    );
  } catch {
    // Shared-list availability must not depend on storage access or quota.
  }
};

export const readSharedListSessionCache = (
  listId: string,
): SharedListSessionCache | null => {
  const stored = readStoredCache(listId);
  if (!stored) return null;
  const cachedAt = parseDate(stored.cachedAt);
  const metadata = deserializeMetadata(stored.metadata);
  const items = Array.isArray(stored.items)
    ? stored.items.map(deserializeItem)
    : undefined;
  const expiresAt = metadata?.isPermanent ? undefined : metadata?.expires;
  if (
    !cachedAt ||
    Date.now() - cachedAt.getTime() > CACHE_TTL_MS ||
    (expiresAt !== undefined && expiresAt.getTime() <= Date.now()) ||
    (stored.metadata !== undefined && !metadata) ||
    items?.some((item) => item === null)
  ) {
    clearSharedListSessionCache(listId);
    return null;
  }
  return {
    ...(metadata ? { metadata } : {}),
    ...(items ? { items: items as GroceryItem[] } : {}),
  };
};

export const writeSharedListMetadataToSessionCache = (
  listId: string,
  metadata: ListMetadata,
): void => {
  const existing = readStoredCache(listId);
  writeStoredCache(listId, {
    ...(existing?.items ? { items: existing.items } : {}),
    metadata: {
      ...(metadata.title === undefined ? {} : { title: metadata.title }),
      created: metadata.created.toISOString(),
      ...(metadata.expires === undefined
        ? {}
        : { expires: metadata.expires.toISOString() }),
      accessConfig: metadata.accessConfig,
      isPermanent: metadata.isPermanent,
    },
  });
};

export const writeSharedListItemsToSessionCache = (
  listId: string,
  items: GroceryItem[],
): void => {
  const existing = readStoredCache(listId);
  writeStoredCache(listId, {
    ...(existing?.metadata ? { metadata: existing.metadata } : {}),
    items: items.map((item) => ({
      id: item.id,
      name: item.name,
      category: item.category,
      ...(item.categoryDisplayName === undefined
        ? {}
        : { categoryDisplayName: item.categoryDisplayName }),
      isChecked: item.isChecked,
      ...(item.checkedAt === undefined
        ? {}
        : { checkedAt: item.checkedAt.toISOString() }),
      createdAt: item.createdAt.toISOString(),
      ...(item.categorizationStatus === undefined
        ? {}
        : { categorizationStatus: item.categorizationStatus }),
      ...(item.updatedViaSharing === undefined
        ? {}
        : { updatedViaSharing: item.updatedViaSharing }),
    })),
  });
};

export const clearSharedListSessionCache = (listId: string): void => {
  try {
    storage()?.removeItem(cacheKey(listId));
  } catch {
    // Cleanup is best-effort when browser storage is unavailable.
  }
};
